import type * as Highcharts from 'highcharts';

type LinearGaugeAxis = Highcharts.Axis & {
  left: number;
  top: number;
  len: number;
  translate(value: number | undefined): number;
};

type LinearGaugeSeries = Omit<Highcharts.Series, 'xAxis' | 'yAxis'> & {
  xAxis: LinearGaugeAxis;
  yAxis: LinearGaugeAxis;
  markLine?: Highcharts.SVGElement;
};

declare module 'highcharts' {
  export const seriesTypes: Record<string, { prototype: Record<string, (this: LinearGaugeSeries, ...args: unknown[]) => void> }>;
}

export default function (H: typeof Highcharts) {
  H.seriesType(
    'lineargauge',
    'column',
    {},
    {
      setVisible: function (this: LinearGaugeSeries, ...args: unknown[]) {
        H.seriesTypes.column.prototype.setVisible.apply(this, args);
        if (this.markLine) {
          this.markLine[this.visible ? 'show' : 'hide']();
        }
      },
      drawPoints: function (this: LinearGaugeSeries, ...args: unknown[]) {
        // Draw the Column like always
        H.seriesTypes.column.prototype.drawPoints.apply(this, args);

        // Add a Marker
        let markLine = this.markLine;
        const chart = this.chart,
          inverted = chart.inverted,
          xAxis = this.xAxis,
          yAxis = this.yAxis,
          point = this.points[0], // we know there is only 1 point
          animate = Boolean(markLine);

        // Hide column
        point.graphic!.hide();

        if (!markLine) {
          // Highcharts takes a flat command list at runtime, which its SVGPathArray typing does not describe
          const path = (inverted
            ? ['M', 0, 0, 'L', -5, -5, 'L', 5, -5, 'L', 0, 0, 'L', 0, 0 + xAxis.len]
            : ['M', 0, 0, 'L', -5, -5, 'L', -5, 5, 'L', 0, 0, 'L', xAxis.len, 0]) as unknown as Highcharts.SVGPathArray;
          markLine = this.markLine = chart.renderer
            .path(path)
            .attr({
              fill: this.color,
              stroke: this.color,
              'stroke-width': 1,
            })
            .add();
        }
        const position = {
          translateX: inverted ? xAxis.left + yAxis.translate(point.y) : xAxis.left,
          translateY: inverted ? xAxis.top : yAxis.top + yAxis.len - yAxis.translate(point.y),
        };
        if (animate) {
          markLine.animate(position);
        } else {
          markLine.attr(position);
        }
      },
    },
  );
}
